/**
 * Shared student-metrics engine.
 *
 * ONE implementation of the four dashboard metrics. It moved here from
 * services/parent/students.ts (#505) when the teacher dashboard needed the
 * same numbers: two copies of this maths is how the same child ends up with
 * two different averages on two screens.
 *
 * USED BY: the parent roster (services/parent/students.ts). UAT 8/10 (PR-6)
 * repointed the TEACHER roster to `GET /api/grading/summary` instead (see
 * services/teacher/students.ts) — this engine's progress numerator reads
 * `student_grades` / `activity_answers` under the caller's own JWT, and
 * teacher RLS on those tables is activity-keyed through a class_id that is
 * NULL or wrong-class for most rows, hard-coding teacher-viewed progress to
 * 0%. A parent's RLS is scoped to their own children (not activity-keyed), so
 * it never had that failure mode and stays on this client-side engine.
 *
 * Metrics per student:
 *   progressCompleted — % of activities completed across all enrolled courses.
 *     "Completed" = submitted (activity_answers.time_end) OR graded
 *     (student_grades), so teacher/PT-graded offline work counts (#3). Null
 *     when the student has no enrollments.
 *   semesterAvg — the student's CURRENT grading-period average, read from the
 *     `v_student_period_summary` view (already a true 0-100 percent). Null when
 *     the student has no graded, period-stamped work.
 *   gpa — canonical weighted, per-course GPA from `v_student_summary`, the
 *     database twin of computeStudentGradebook. Null when no graded work.
 *   lastAccess — most recent activity submission.
 *
 * SCALE (why this file is not a naive lift of the parent version):
 *
 *   1. NO N+1. The average used to be one `GET /api/grading/student-grades/
 *      :studentId/:gradeLevelId` call PER student, fired in a Promise.all. Fine
 *      for a parent with 2 children, 35 concurrent HTTP calls for a teacher's
 *      class. It is now ONE PostgREST read of `v_student_period_summary` for
 *      the whole cohort. The per-student endpoint survives only as a narrow
 *      fallback — see RESOLVING THE CURRENT PERIOD below.
 *
 *   2. NO SILENT TRUNCATION. Every bulk `.in()` read goes through
 *      fetchAllRowsIn, which chunks the id list (URL length) and pages past
 *      PostgREST's 1000-row cap. Before, a class whose activities/answers/
 *      grades exceeded 1000 rows got a silently truncated result and therefore
 *      a wrong percentage — with `error: null`.
 *
 *   3. NO DOUBLE-COUNTING / STALE ENROLLMENTS (UAT 8/10, PR-6). The
 *      course_enrollments read now (a) excludes `status = 'withdrawn'` rows —
 *      a withdrawn course's activities were still landing in the progress
 *      denominator — and (b) de-dupes the per-student course id list before
 *      summing activity counts, so a duplicate (student_id, course_id) row
 *      can no longer count one course's activities twice. Both bugs only
 *      ever INFLATE the denominator, so both fixes only ever move a
 *      student's progress UP — this is an intended correction, not a
 *      regression, and shifts some parent-visible numbers slightly (e.g. one
 *      student measured 8% -> 12%).
 *
 * RESOLVING THE CURRENT PERIOD (the view returns MULTIPLE rows per student):
 *
 *   `v_student_period_summary` has one row per (student, grading period). Its
 *   `period` column is the 1-based rank of the period WITHIN its school year,
 *   so a student with grades in two school years has two rows both labelled
 *   period "1" — different `period_id`, same rank. Picking by rank would
 *   multiply across school years and could surface last year's average.
 *
 *   We therefore key on `period_id` and resolve, in order:
 *     a. Restrict to rows whose grading_period belongs to the student's ACTIVE
 *        school year (student_grade_levels.school_year_id where active). If the
 *        student has no rows in that school year, fall back to all their rows
 *        rather than showing nothing.
 *     b. Within that pool, prefer the period that contains TODAY
 *        (start_date <= today <= end_date, compared on the date portion so a
 *        `date` and a timestamp both behave inclusively).
 *     c. If today falls outside every period (summer, gap week, stale data),
 *        take the LATEST period by start_date — the most recently reported one.
 *     d. If the student has no view row at all but does have an active grade
 *        level, fall back to the per-student server gradebook endpoint. That
 *        covers the ~1% of grades with a NULL period_id, which the view drops
 *        (`WHERE period IS NOT NULL`) — without it those students would read
 *        "—" despite having graded work. It fires only for students the bulk
 *        read could not answer, so the common path stays at zero extra calls.
 */
import { supabase } from "../supabase";
import { fetchAllRowsIn, fetchAllRowsInCounted } from "./fetchAllRows";

// Lazy import to avoid a circular dependency with api.ts (same pattern as grades.ts)
const getApi = () => import("../api").then((m) => m.api);

/** The four numbers every student-roster dashboard renders. */
export interface StudentMetrics {
  progressCompleted: number | null;
  semesterAvg: number | null;
  periodAvgs: Record<string, number>;
  gpa: string | number | null;
  lastAccess: string | null;
}

/**
 * DataTable row envelope shared by the parent and teacher rosters.
 *
 * NOTE: the field name "semsester_avg" is intentionally misspelled — every
 * consumer across the app reads this exact key. Do NOT rename it.
 */
export interface StudentMetricsRow {
  id: string;
  type: "student_progress_data";
  attributes: {
    first_name: string;
    last_name: string;
    email: string | null;
    user_name: string | null;
    status: string | null;
    progress: { completed: number | null };
    semsester_avg: number | null;
    period_avgs?: Record<string, number>;
    gpa: string | number | null;
    last_access: string | null;
  };
}

// ---------------------------------------------------------------------------
// Completed-activity set
// ---------------------------------------------------------------------------

/**
 * Per-student "completed activities" set.
 *
 * An activity counts as COMPLETED for a student when they've EITHER submitted it
 * (an activity_answers row with time_end) OR it's been graded (a student_grades
 * row). Before #3 only submissions counted, so a teacher/parent-teacher-graded
 * OFFLINE activity showed 0% progress even though the work was done and scored.
 * Union + dedup by activity id, keyed by student.
 */
export function buildCompletedActivities(
  answers: ReadonlyArray<{ student_id: string | null; activity_id: string | null; time_end: string | null }>,
  grades: ReadonlyArray<{ student_id: string | null; activity_id: string | null }>,
): Map<string, Set<string>> {
  const completed = new Map<string, Set<string>>();
  const add = (studentId: string | null, activityId: string | null) => {
    if (!studentId || !activityId) return;
    let set = completed.get(studentId);
    if (!set) {
      set = new Set<string>();
      completed.set(studentId, set);
    }
    set.add(activityId);
  };
  for (const a of answers) if (a.time_end) add(a.student_id, a.activity_id);
  for (const g of grades) add(g.student_id, g.activity_id);
  return completed;
}

// ---------------------------------------------------------------------------
// Row shapes of the reads below (explicit, so nothing leans on `any`)
// ---------------------------------------------------------------------------

interface EnrollmentRow {
  student_id: string | null;
  course_id: string | null;
}
interface ActivityRow {
  id: string;
  course_id: string | null;
}
interface AnswerRow {
  student_id: string | null;
  activity_id: string | null;
  time_end: string | null;
}
interface GradeRow {
  student_id: string | null;
  grade: number | null;
  activity_id: string | null;
}
interface GradeLevelRow {
  student_id: string | null;
  grade_level_id: string | null;
  school_year_id: string | null;
}
interface PeriodSummaryRow {
  student_id: string | null;
  period_id: string | null;
  period_avg: number | null;
}
interface StudentSummaryRow {
  student_id: string | null;
  gpa: number | null;
}
interface GradingPeriodRow {
  id: string;
  school_year_id: string | null;
  start_date: string | null;
  end_date: string | null;
}

/** Compare on the date portion so a `date` column and an ISO timestamp both work. */
const day = (value: string | null | undefined): string | null =>
  value ? value.slice(0, 10) : null;

// ---------------------------------------------------------------------------
// The engine
// ---------------------------------------------------------------------------

/**
 * Compute per-student progress, current-period average, GPA band and last
 * access in bulk. Every student id passed in is present in the returned map
 * (all-null when there is nothing to report), so callers never null-check keys.
 */
export async function computeChildMetrics(
  studentIds: string[],
): Promise<Map<string, StudentMetrics>> {
  const result = new Map<string, StudentMetrics>();
  for (const id of studentIds) {
    result.set(id, { progressCompleted: null, semesterAvg: null, periodAvgs: {}, gpa: null, lastAccess: null });
  }

  if (studentIds.length === 0) return result;

  const [
    { data: enrollments, error: enrollErr },
    { data: answers, error: ansErr },
    { data: grades, error: gradeErr },
    { data: sgls },
    { data: periodRows },
    { data: studentSummaryRows },
  ] = await Promise.all([
    fetchAllRowsIn<EnrollmentRow>(studentIds, (ids, from, to) =>
      supabase
        .from("course_enrollments")
        .select("student_id, course_id")
        .in("student_id", ids)
        .neq("status", "withdrawn")
        .range(from, to),
    ),
    fetchAllRowsIn<AnswerRow>(studentIds, (ids, from, to) =>
      supabase
        .from("activity_answers")
        .select("student_id, activity_id, time_end")
        .in("student_id", ids)
        .not("time_end", "is", null)
        .range(from, to),
    ),
    fetchAllRowsIn<GradeRow>(studentIds, (ids, from, to) =>
      supabase
        .from("student_grades")
        .select("student_id, grade, activity_id")
        .in("student_id", ids)
        .range(from, to),
    ),
    fetchAllRowsIn<GradeLevelRow>(studentIds, (ids, from, to) =>
      supabase
        .from("student_grade_levels")
        .select("student_id, grade_level_id, school_year_id")
        .in("student_id", ids)
        .eq("active", true)
        .range(from, to),
    ),
    fetchAllRowsIn<PeriodSummaryRow>(studentIds, (ids, from, to) =>
      supabase
        .from("v_student_period_summary")
        .select("student_id, period_id, period_avg")
        .in("student_id", ids)
        .range(from, to),
    ),
    fetchAllRowsIn<StudentSummaryRow>(studentIds, (ids, from, to) =>
      supabase
        .from("v_student_summary")
        .select("student_id, gpa")
        .in("student_id", ids)
        .range(from, to),
    ),
  ]);
  if (enrollErr) throw enrollErr;
  if (ansErr) throw ansErr;
  if (gradeErr) throw gradeErr;

  const studentCourseIds = new Map<string, string[]>();
  for (const e of enrollments) {
    if (!e.student_id || !e.course_id) continue;
    const existing = studentCourseIds.get(e.student_id) ?? [];
    if (!existing.includes(e.course_id)) existing.push(e.course_id);
    studentCourseIds.set(e.student_id, existing);
  }

  const allCourseIds = Array.from(
    new Set(enrollments.map((e) => e.course_id).filter((c): c is string => Boolean(c))),
  );
  const referencedPeriodIds = Array.from(
    new Set(periodRows.map((r) => r.period_id).filter((p): p is string => Boolean(p))),
  );

  const activityPending: Promise<{ data: ActivityRow[]; error: unknown }> =
    allCourseIds.length > 0
      ? fetchAllRowsInCounted<ActivityRow>(allCourseIds, (ids, from, to) =>
          supabase
            .from("activities")
            .select("id, course_id", { count: "exact" })
            .in("course_id", ids)
            .order("id")
            .range(from, to),
        ).catch((error: Error) => ({ data: [], error }))
      : Promise.resolve({ data: [], error: null });

  const noPeriods: GradingPeriodRow[] = [];

  const periodRead =
    referencedPeriodIds.length > 0
      ? await fetchAllRowsIn<GradingPeriodRow>(referencedPeriodIds, (ids, from, to) =>
          supabase
            .from("grading_periods")
            .select("id, school_year_id, start_date, end_date")
            .in("id", ids)
            .range(from, to),
        )
      : { data: noPeriods, error: null };

  const studentLastAccess = new Map<string, string>();
  for (const a of answers) {
    if (!a.student_id) continue;
    const prev = studentLastAccess.get(a.student_id);
    if (a.time_end && (!prev || a.time_end > prev)) {
      studentLastAccess.set(a.student_id, a.time_end);
    }
  }

  const completedActivities = buildCompletedActivities(answers, grades);
  const gradedStudents = new Set(grades.map((g) => g.student_id).filter((id): id is string => Boolean(id)));

  const gradeLevelByStudent = new Map<string, string>();
  const schoolYearByStudent = new Map<string, string>();
  for (const sgl of sgls) {
    if (!sgl.student_id) continue;
    if (sgl.grade_level_id && !gradeLevelByStudent.has(sgl.student_id)) {
      gradeLevelByStudent.set(sgl.student_id, sgl.grade_level_id);
    }
    if (sgl.school_year_id && !schoolYearByStudent.has(sgl.student_id)) {
      schoolYearByStudent.set(sgl.student_id, sgl.school_year_id);
    }
  }

  const summaryByStudent = new Map<string, PeriodSummaryRow[]>();
  for (const row of periodRows) {
    if (!row.student_id || !row.period_id) continue;
    const list = summaryByStudent.get(row.student_id) ?? [];
    list.push(row);
    summaryByStudent.set(row.student_id, list);
  }

  const periodById = new Map<string, GradingPeriodRow>();
  for (const p of periodRead.data) periodById.set(p.id, p);

  const today = new Date().toISOString().slice(0, 10);

  /** Apply the a→c ladder documented at the top of this file. */
  const pickCurrentAvg = (studentId: string): number | null => {
    const rows = summaryByStudent.get(studentId) ?? [];
    if (rows.length === 0) return null;

    // (a) prefer rows in the student's ACTIVE school year
    const activeSchoolYear = schoolYearByStudent.get(studentId);
    const inActiveYear = activeSchoolYear
      ? rows.filter((r) => periodById.get(r.period_id ?? "")?.school_year_id === activeSchoolYear)
      : [];
    const pool = inActiveYear.length > 0 ? inActiveYear : rows;

    // (b) the period containing today
    const current = pool.find((r) => {
      const p = periodById.get(r.period_id ?? "");
      const start = day(p?.start_date);
      const end = day(p?.end_date);
      return Boolean(start && end && start <= today && today <= end);
    });

    // (c) otherwise the latest period by start_date (rows with no known period
    //     metadata sort last, so a dated period always wins over an unknown one)
    const chosen =
      current ??
      [...pool].sort((a, b) => {
        const aStart = day(periodById.get(a.period_id ?? "")?.start_date) ?? "";
        const bStart = day(periodById.get(b.period_id ?? "")?.start_date) ?? "";
        return bStart.localeCompare(aStart);
      })[0];

    const avg = chosen?.period_avg;
    return typeof avg === "number" && Number.isFinite(avg) ? avg : null;
  };

  const periodAvgByStudent = new Map<string, number>();
  const gpaByStudent = new Map<string, number>();
  for (const row of studentSummaryRows) {
    if (row.student_id && typeof row.gpa === "number" && Number.isFinite(row.gpa)) {
      gpaByStudent.set(row.student_id, row.gpa);
    }
  }
  const needsServerFallback: string[] = [];
  for (const studentId of studentIds) {
    const avg = pickCurrentAvg(studentId);
    if (avg != null) {
      periodAvgByStudent.set(studentId, avg);
      // Compatibility fallback while the additive canonical view migration is
      // rolling out. Once v_student_summary is present it always wins.
      if (!gpaByStudent.has(studentId)) gpaByStudent.set(studentId, avg);
    }
    if (avg == null && !gpaByStudent.has(studentId) && gradeLevelByStudent.has(studentId) && gradedStudents.has(studentId)) {
      // (d) no usable view row — grades may exist but be un-period-stamped.
      needsServerFallback.push(studentId);
    }
  }

  // ── 4d. Narrow per-student fallback (see (d) in the header comment) ──────
  if (needsServerFallback.length > 0) {
    const api = await getApi();
    await Promise.all(
      needsServerFallback.map(async (sid) => {
        const gradeLevelId = gradeLevelByStudent.get(sid);
        if (!gradeLevelId) return;
        try {
          const res = await api.get(`/api/grading/student-grades/${sid}/${gradeLevelId}`);
          const gpa = res?.data?.total_gpa;
          if (typeof gpa === "number" && Number.isFinite(gpa)) {
            if (!periodAvgByStudent.has(sid)) periodAvgByStudent.set(sid, gpa);
            if (!gpaByStudent.has(sid)) gpaByStudent.set(sid, gpa);
          }
        } catch {
          // endpoint 404 / auth error / network — graceful fallback (leave unset)
        }
      }),
    );
  }

  const activityRead = await activityPending;

  if (activityRead.error) throw activityRead.error;

  const courseActivityCount = new Map<string, number>();

  for (const a of activityRead.data) {
    if (!a.course_id) continue;
    courseActivityCount.set(a.course_id, (courseActivityCount.get(a.course_id) ?? 0) + 1);
  }

  // ── 5. Assemble per-student metrics ──────────────────────────────────────
  for (const studentId of studentIds) {
    const courseIds = studentCourseIds.get(studentId) ?? [];

    const totalActivities = courseIds.reduce(
      (sum, cid) => sum + (courseActivityCount.get(cid) ?? 0),
      0,
    );
    const completedCount = completedActivities.get(studentId)?.size ?? 0;
    const progressCompleted =
      totalActivities > 0 ? Math.round((completedCount / totalActivities) * 100) : null;

    // Current-period average, already a true 0-100 percent. Rounded to an
    // integer to match the existing shape (consumers render `${semsester_avg}%`).
    const rawAvg = periodAvgByStudent.get(studentId);
    const semesterAvg = rawAvg == null ? null : Math.round(rawAvg);

    // Preserve the two-decimal display contract used by roster cells while the
    // underlying value now comes from the canonical weighted GPA view.
    const rawGpa = gpaByStudent.get(studentId);
    const gpa = rawGpa == null ? null : rawGpa.toFixed(2);

    const periodAvgs: Record<string, number> = {};
    for (const row of summaryByStudent.get(studentId) ?? []) {
      if (row.period_id && typeof row.period_avg === "number" && Number.isFinite(row.period_avg)) {
        periodAvgs[row.period_id] = row.period_avg;
      }
    }

    result.set(studentId, {
      progressCompleted,
      semesterAvg,
      periodAvgs,
      gpa,
      lastAccess: studentLastAccess.get(studentId) ?? null,
    });
  }

  return result;
}
